import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Plus, Zap, Calendar, CheckCircle2, Clock, PlayCircle, 
  ChevronDown, ChevronRight, X, Target, MoreVertical, Edit2
} from 'lucide-react';
import { toast } from 'sonner';
import { projectAPI } from '../lib/api';

const sprintStatuses = {
  planning: { label: 'Planning', color: 'text-gray-400 bg-gray-500/10 border-gray-500/20' },
  active: { label: 'Active', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20' },
  completed: { label: 'Completed', color: 'text-blue-400 bg-blue-500/10 border-blue-500/20' },
};

const taskStatuses = {
  todo: { label: 'To Do', color: 'bg-slate-400' },
  in_progress: { label: 'In Progress', color: 'bg-blue-400' },
  review: { label: 'Review', color: 'bg-amber-400' },
  done: { label: 'Done', color: 'bg-emerald-400' },
};

export default function SprintsPanel({ projectId, tasks = [], onTasksChange }) {
  const [sprints, setSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedSprint, setExpandedSprint] = useState(null);
  const [newSprint, setNewSprint] = useState({
    name: '',
    goal: '',
    start_date: '',
    end_date: '',
    status: 'planning',
  });

  useEffect(() => {
    loadSprints();
  }, [projectId]);

  useEffect(() => {
    // Auto-expand active sprint
    const activeSprint = sprints.find(s => s.status === 'active');
    if (activeSprint && !expandedSprint) {
      setExpandedSprint(activeSprint.id);
    }
  }, [sprints]);

  const loadSprints = async () => {
    try {
      const res = await projectAPI.getSprints(projectId);
      setSprints(res.data || []);
    } catch (err) {
      console.log('Loading sprints:', err);
      setSprints([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSprint = async () => {
    if (!newSprint.name.trim()) {
      toast.error('Sprint name is required');
      return;
    }
    try {
      await projectAPI.addSprint(projectId, newSprint);
      setNewSprint({ name: '', goal: '', start_date: '', end_date: '', status: 'planning' });
      setShowAddForm(false);
      await loadSprints();
      toast.success('Sprint created');
    } catch (err) {
      toast.error('Failed to create sprint');
    }
  };

  const handleUpdateSprint = async (sprintId, updates) => {
    try {
      await projectAPI.updateSprint(projectId, sprintId, updates);
      await loadSprints();
      toast.success('Sprint updated');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleDeleteSprint = async (sprintId) => {
    try {
      await projectAPI.deleteSprint(projectId, sprintId);
      await loadSprints();
      toast.success('Sprint deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleStartSprint = async (sprintId) => {
    // First, complete any active sprint
    const activeSprint = sprints.find(s => s.status === 'active');
    if (activeSprint) {
      await handleUpdateSprint(activeSprint.id, { status: 'completed' });
    }
    await handleUpdateSprint(sprintId, { status: 'active' });
  };

  const handleCompleteSprint = async (sprintId) => {
    await handleUpdateSprint(sprintId, { status: 'completed' });
  };

  const getSprintTasks = (sprintId) => tasks.filter(t => t.sprint_id === sprintId);
  const getBacklogTasks = () => tasks.filter(t => !t.sprint_id);

  const getSprintProgress = (sprintId) => {
    const sprintTasks = getSprintTasks(sprintId);
    if (sprintTasks.length === 0) return 0;
    const done = sprintTasks.filter(t => t.status === 'done').length;
    return Math.round((done / sprintTasks.length) * 100);
  };

  const activeSprint = sprints.find(s => s.status === 'active');
  const planningSprints = sprints.filter(s => s.status === 'planning');
  const completedSprints = sprints.filter(s => s.status === 'completed');

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-[var(--accent-indigo)] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="h-full flex flex-col" data-testid="sprints-panel">
      {/* Header */}
      <div className="p-4 border-b border-[var(--surface-border)]">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-left flex items-center gap-2">
              <Zap className="w-5 h-5 text-amber-400" /> Sprints
            </h2>
            <p className="text-xs opacity-60 text-left">Manage sprints and track velocity</p>
          </div>
          <Button size="sm" onClick={() => setShowAddForm(true)}>
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Sprint
          </Button>
        </div>

        {/* Stats */}
        <div className="flex gap-3">
          <div className="px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
            <div className="text-xs text-emerald-400">Active Sprint</div>
            <div className="text-sm font-semibold">{activeSprint?.name || 'None'}</div>
          </div>
          <div className="px-3 py-2 rounded-lg bg-[var(--surface-card)] border border-[var(--surface-border)]">
            <div className="text-xs opacity-50">Backlog</div>
            <div className="text-sm font-semibold">{getBacklogTasks().length} tasks</div>
          </div>
          <div className="px-3 py-2 rounded-lg bg-[var(--surface-card)] border border-[var(--surface-border)]">
            <div className="text-xs opacity-50">Completed</div>
            <div className="text-sm font-semibold">{completedSprints.length} sprints</div>
          </div>
        </div>
      </div>

      {/* Add Sprint Form */}
      {showAddForm && (
        <div className="p-4 border-b border-[var(--surface-border)] bg-[var(--surface-card)]">
          <div className="grid grid-cols-2 gap-3 max-w-2xl">
            <div className="col-span-2">
              <label className="text-xs font-medium mb-1 block text-left">Sprint Name *</label>
              <Input
                value={newSprint.name}
                onChange={(e) => setNewSprint({ ...newSprint, name: e.target.value })}
                placeholder="e.g., Sprint 1, MVP Release"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium mb-1 block text-left">Sprint Goal</label>
              <Textarea
                value={newSprint.goal}
                onChange={(e) => setNewSprint({ ...newSprint, goal: e.target.value })}
                placeholder="What do you want to achieve in this sprint?"
                rows={2}
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-left">Start Date</label>
              <Input
                type="date"
                value={newSprint.start_date}
                onChange={(e) => setNewSprint({ ...newSprint, start_date: e.target.value })}
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block text-left">End Date</label>
              <Input
                type="date"
                value={newSprint.end_date}
                onChange={(e) => setNewSprint({ ...newSprint, end_date: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
            <Button onClick={handleCreateSprint}>Create Sprint</Button>
          </div>
        </div>
      )}

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* Active Sprint */}
          {activeSprint && (
            <div className="rounded-lg border-2 border-emerald-500/30 bg-emerald-500/5 overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-emerald-500/10"
                onClick={() => setExpandedSprint(expandedSprint === activeSprint.id ? null : activeSprint.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {expandedSprint === activeSprint.id ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    <PlayCircle className="w-5 h-5 text-emerald-400" />
                    <span className="font-semibold">{activeSprint.name}</span>
                    <Badge className={sprintStatuses.active.color}>Active</Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-xs opacity-60">
                      <Calendar className="w-4 h-4" />
                      {activeSprint.end_date ? new Date(activeSprint.end_date).toLocaleDateString() : 'No end date'}
                    </div>
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); handleCompleteSprint(activeSprint.id); }}>
                      Complete Sprint
                    </Button>
                  </div>
                </div>
                {activeSprint.goal && (
                  <p className="text-sm opacity-60 mt-2 ml-8 text-left">{activeSprint.goal}</p>
                )}
                {/* Progress */}
                <div className="mt-3 ml-8 flex items-center gap-3">
                  <div className="flex-1 max-w-xs h-2 bg-[var(--surface-border)] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-emerald-400 rounded-full transition-all"
                      style={{ width: `${getSprintProgress(activeSprint.id)}%` }}
                    />
                  </div>
                  <span className="text-xs">{getSprintProgress(activeSprint.id)}%</span>
                  <span className="text-xs opacity-50">
                    {getSprintTasks(activeSprint.id).filter(t => t.status === 'done').length}/{getSprintTasks(activeSprint.id).length} tasks
                  </span>
                </div>
              </div>

              {/* Expanded Tasks */}
              {expandedSprint === activeSprint.id && (
                <div className="border-t border-emerald-500/20 p-4 bg-[var(--surface-bg)]/50">
                  <h4 className="text-xs font-semibold opacity-50 uppercase mb-3 text-left">Sprint Tasks</h4>
                  <div className="space-y-2">
                    {getSprintTasks(activeSprint.id).length > 0 ? (
                      getSprintTasks(activeSprint.id).map(task => (
                        <div key={task.id} className="flex items-center gap-3 p-2 rounded bg-[var(--surface-card)] border border-[var(--surface-border)]">
                          <div className={`w-2 h-2 rounded-full ${taskStatuses[task.status]?.color || 'bg-gray-400'}`} />
                          <span className="text-sm flex-1 text-left">{task.title}</span>
                          <Badge variant="outline" className="text-[10px]">{taskStatuses[task.status]?.label || task.status}</Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm opacity-50 text-center py-4">No tasks in this sprint. Add tasks from the Tasks panel.</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Planning Sprints */}
          {planningSprints.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold opacity-50 mb-2 text-left">Planning ({planningSprints.length})</h3>
              <div className="space-y-2">
                {planningSprints.map(sprint => (
                  <div key={sprint.id} className="rounded-lg border border-[var(--surface-border)] bg-[var(--surface-card)] overflow-hidden">
                    <div
                      className="p-3 cursor-pointer hover:bg-[var(--surface-bg)]/50"
                      onClick={() => setExpandedSprint(expandedSprint === sprint.id ? null : sprint.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {expandedSprint === sprint.id ? <ChevronDown className="w-4 h-4 opacity-50" /> : <ChevronRight className="w-4 h-4 opacity-50" />}
                          <span className="font-medium">{sprint.name}</span>
                          <Badge className={sprintStatuses.planning.color}>Planning</Badge>
                          <span className="text-xs opacity-50">({getSprintTasks(sprint.id).length} tasks)</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" className="text-xs h-7" onClick={(e) => { e.stopPropagation(); handleStartSprint(sprint.id); }}>
                            <PlayCircle className="w-3 h-3 mr-1" /> Start Sprint
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 text-red-400" onClick={(e) => { e.stopPropagation(); handleDeleteSprint(sprint.id); }}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                    {expandedSprint === sprint.id && (
                      <div className="border-t border-[var(--surface-border)] p-3 bg-[var(--surface-bg)]/30">
                        {sprint.goal && <p className="text-sm opacity-60 mb-3 text-left">{sprint.goal}</p>}
                        <div className="space-y-1">
                          {getSprintTasks(sprint.id).map(task => (
                            <div key={task.id} className="flex items-center gap-2 px-2 py-1.5 rounded bg-[var(--surface-card)] border border-[var(--surface-border)]">
                              <div className={`w-1.5 h-1.5 rounded-full ${taskStatuses[task.status]?.color || 'bg-gray-400'}`} />
                              <span className="text-xs flex-1 text-left">{task.title}</span>
                              <Badge variant="outline" className="text-[10px]">{task.status}</Badge>
                            </div>
                          ))}
                          {getSprintTasks(sprint.id).length === 0 && (
                            <p className="text-xs opacity-40 py-2 text-center">No tasks assigned</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completed Sprints */}
          {completedSprints.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold opacity-50 mb-2 text-left">Completed ({completedSprints.length})</h3>
              <div className="space-y-2">
                {completedSprints.map(sprint => (
                  <div key={sprint.id} className="rounded-lg border border-[var(--surface-border)] bg-[var(--surface-card)] p-3 opacity-60">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-4 h-4 text-blue-400" />
                        <span className="font-medium">{sprint.name}</span>
                        <Badge className={sprintStatuses.completed.color}>Completed</Badge>
                      </div>
                      <span className="text-xs">{getSprintTasks(sprint.id).length} tasks</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {sprints.length === 0 && (
            <div className="text-center py-12 opacity-60">
              <Zap className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No sprints yet</p>
              <p className="text-xs mt-1">Create your first sprint to start organizing work</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
